import React, { useEffect, useMemo, useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { feedbackApi, FeedbackEntity, FeedbackFilters } from "../../services/feedbackApi";
import { RefreshCw } from "lucide-react";

type RatingFilter = "all" | "5" | "4" | "3" | "2" | "1" | "unrated";
type TypeFilter = "all" | "feedback" | "bug";
type TimeframeFilter = "7" | "30" | "90" | "all";

const ratingOptions: Array<{ value: RatingFilter; label: string }> = [
  { value: "all", label: "All ratings" },
  { value: "5", label: "5 – Excellent" },
  { value: "4", label: "4 – Great" },
  { value: "3", label: "3 – Okay" },
  { value: "2", label: "2 – Needs work" },
  { value: "1", label: "1 – Critical" },
  { value: "unrated", label: "No rating" },
];

const typeOptions: Array<{ value: TypeFilter; label: string }> = [
  { value: "all", label: "All types" },
  { value: "feedback", label: "Feedback" },
  { value: "bug", label: "Bug reports" },
];

const timeframeOptions: Array<{ value: TimeframeFilter; label: string }> = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

const timeframeToDays = (value: TimeframeFilter): number => {
  switch (value) {
    case "7":
      return 7;
    case "30":
      return 30;
    case "90":
      return 90;
    default:
      return Number.POSITIVE_INFINITY;
  }
};

const formatRelativeTime = (isoDate: string): string => {
  const submitted = new Date(isoDate).getTime();
  const now = Date.now();
  const diffMs = submitted - now;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [day, "day"],
    [hour, "hour"],
    [minute, "minute"],
  ];

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [duration, unit] of units) {
    if (Math.abs(diffMs) >= duration || unit === "minute") {
      return rtf.format(Math.round(diffMs / duration), unit);
    }
  }
  return "just now";
};

const formatDateTime = (isoDate: string): string =>
  new Date(isoDate).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const getRatingDisplay = (rating: number | null): string => {
  if (rating === null || rating === undefined) {
    return "Unrated";
  }
  const filled = "★".repeat(Math.max(0, Math.min(rating, 5)));
  const empty = "☆".repeat(5 - Math.max(0, Math.min(rating, 5)));
  return `${filled}${empty}`;
};

const filterInsights = (
  insights: any[],
  searchTerm: string,
  ratingFilter: RatingFilter,
  typeFilter: TypeFilter,
  timeframe: TimeframeFilter,
): any[] => {
  const daysLimit = timeframeToDays(timeframe);
  const hasSearch = searchTerm.trim().length > 0;
  const query = searchTerm.trim().toLowerCase();

  return insights.filter((insight) => {
    if (daysLimit !== Number.POSITIVE_INFINITY) {
      const submittedDate = new Date(insight.submittedAt);
      const diffDays =
        (Date.now() - submittedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays > daysLimit) {
        return false;
      }
    }

    if (typeFilter !== "all" && insight.type !== typeFilter) {
      return false;
    }

    if (ratingFilter !== "all") {
      if (ratingFilter === "unrated" && insight.rating !== null) {
        return false;
      }
      if (
        ratingFilter !== "unrated" &&
        insight.rating?.toString() !== ratingFilter
      ) {
        return false;
      }
    }

    if (hasSearch) {
      const matchesQuery =
        insight.comment.toLowerCase().includes(query) ||
        insight.pageUrl.toLowerCase().includes(query) ||
        insight.email.toLowerCase().includes(query) ||
        insight.labels?.some((label: string) => label.toLowerCase().includes(query));
      if (!matchesQuery) {
        return false;
      }
    }

    return true;
  });
};

const FeedbackInsightsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [timeframe, setTimeframe] = useState<TimeframeFilter>("30");
  const [feedbackData, setFeedbackData] = useState<FeedbackEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [preview, setPreview] = useState<{
    image: string;
    page: string;
    email: string;
  } | null>(null);
  const [products, setProducts] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("all");
  const [productsLoading, setProductsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load products from API
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setProductsLoading(true);
        const response = await feedbackApi.getProducts();
        setProducts(response.products || []);
      } catch (err) {
        console.error('Failed to load products:', err);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    loadProducts();
  }, [refreshKey]);

  // Load feedback data from Azure API
  // NOTE: Permission filter has been commented out to allow all authenticated users to see all feedback
  useEffect(() => {
    const loadFeedback = async () => {
      const timeframeDays = timeframe === "all" ? undefined : timeframeToDays(timeframe);
      const filters: FeedbackFilters = {};
      
      if (timeframeDays !== undefined) {
        filters.timeframe = timeframeDays;
      }

      // Add product filter if a specific product is selected
      if (selectedProduct && selectedProduct !== "all") {
        filters.productId = selectedProduct;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Test API connection first
        const connectionTest = await feedbackApi.testConnection();
        
        if (connectionTest.status === 'error') {
          console.warn('API connection test failed, but proceeding with data request');
        }
        
        const response = await feedbackApi.getFeedback(filters);
        
        setFeedbackData(response.feedback);
        setUserInfo(response.user);
        
      } catch (err) {
        console.error('Failed to load feedback:', err);
        console.error('Error details:', {
          errorType: err?.constructor?.name,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorStack: err instanceof Error ? err.stack : undefined,
          filters: filters,
          timeframe
        });
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
        setFeedbackData([]);
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, [timeframe, selectedProduct, refreshKey]);

  // Convert Azure feedback entity to local format for existing UI
  const convertToLocalFormat = (entities: FeedbackEntity[]) => {
    return entities.map(entity => {
      const attachmentUrls = typeof entity.attachmentUrls === 'string' 
        ? JSON.parse(entity.attachmentUrls || '[]') 
        : entity.attachmentUrls || [];
      const metadata = typeof entity.metadata === 'string' 
        ? JSON.parse(entity.metadata || '{}') 
        : entity.metadata || {};
      
      return {
        id: entity.id,
        submittedAt: entity.submittedAt,
        rating: entity.rating,
        type: entity.type,
        comment: entity.comment,
        pageUrl: entity.pageUrl,
        email: entity.email,
        hasScreenshot: metadata.hasScreenshot || false,
        harCaptured: metadata.hasHarFile || false,
        consoleLogCount: metadata.consoleLogs || 0,
        networkRequestCount: metadata.networkRequests || 0,
        labels: metadata.selectedElement?.textContent ? [metadata.selectedElement.textContent.slice(0, 24)] : undefined,
        // For attachments, use the URLs from blob storage
        screenshot: attachmentUrls.find((url: string) => url.includes('screenshot')),
        harFile: undefined, // HAR files should be downloaded via separate endpoint
        consoleLogs: undefined, // Console logs should be downloaded via separate endpoint  
        networkRequests: undefined // Network requests should be downloaded via separate endpoint
      };
    });
  };

  const mergedInsights = useMemo(
    () => convertToLocalFormat(feedbackData),
    [feedbackData]
  );

  const filteredInsights = useMemo(
    () =>
      filterInsights(
        mergedInsights,
        searchTerm,
        ratingFilter,
        typeFilter,
        timeframe,
      ),
    [mergedInsights, searchTerm, ratingFilter, typeFilter, timeframe],
  );

  const totals = useMemo(() => {
    const total = filteredInsights.length;
    const bugCount = filteredInsights.filter(
      (item) => item.type === "bug",
    ).length;
    const feedbackCount = filteredInsights.filter(
      (item) => item.type === "feedback",
    ).length;
    const ratedItems = filteredInsights.filter(
      (item) => typeof item.rating === "number",
    );
    const averageRating =
      ratedItems.length > 0
        ? ratedItems.reduce((sum, item) => sum + (item.rating ?? 0), 0) /
          ratedItems.length
        : null;

    const pages = filteredInsights.reduce<Record<string, number>>(
      (acc, item) => {
        acc[item.pageUrl] = (acc[item.pageUrl] || 0) + 1;
        return acc;
      },
      {},
    );

    const topPages = Object.entries(pages)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([page, count]) => ({ page, count }));

    return {
      total,
      bugCount,
      feedbackCount,
      averageRating,
      topPages,
    };
  }, [filteredInsights]);

  const handleDownloadHar = (harFile: string, id: string) => {
    const blob = new Blob([harFile], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `feedback-${id}.har.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadJson = (data: unknown, id: string, suffix: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `feedback-${id}-${suffix}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-foreground">
            Feedback insights
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading || productsLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || productsLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Review feedback captured through the widget from various products.
          Data is stored securely in Azure.
          {/* NOTE: Permission filter has been commented out to allow all authenticated users to see all feedback */}
          {/* Data is filtered based on your access permissions. */}
          {userInfo && (
            <span className="block mt-1">
              Logged in as: <strong>{userInfo.email}</strong>
              {userInfo.isAdmin && <span className="text-green-600 ml-2">(Admin)</span>}
            </span>
          )}
        </p>
      </section>

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="text-muted-foreground">Loading feedback data...</p>
          </div>
        </div>
      ) : (
        <>
          <Card>
        <CardHeader className="pb-4">
          <CardDescription>Summary</CardDescription>
          <CardTitle className="text-3xl font-bold text-foreground">
            {totals.total}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Total submissions
            {timeframe === "all"
              ? " (all time)"
              : ` in the last ${timeframe} days`}
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">Bug reports</p>
            <p className="text-xl font-semibold text-foreground">
              {totals.bugCount}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Feedback entries</p>
            <p className="text-xl font-semibold text-foreground">
              {totals.feedbackCount}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Average rating</p>
            <p className="text-xl font-semibold text-foreground">
              {totals.averageRating ? totals.averageRating.toFixed(1) : "–"}
            </p>
          </div>
          {totals.topPages.length > 0 && (
            <div className="md:col-span-3 space-y-2">
              <p className="text-sm text-muted-foreground">
                Most-mentioned pages
              </p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {totals.topPages.map((item) => (
                  <li
                    key={item.page}
                    className="rounded-full border border-border bg-muted/40 px-3 py-1"
                  >
                    <span className="font-medium text-foreground">
                      {item.page}
                    </span>
                    <span className="ml-1 text-muted-foreground">
                      ({item.count})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4 shadow-sm md:flex-row md:items-center md:justify-between">
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search comments, pages, email addresses, or labels"
          className="md:w-80"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={selectedProduct}
            onValueChange={(value: string) => setSelectedProduct(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={productsLoading ? "Loading..." : "Select product"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {products.map((product) => (
                <SelectItem key={product} value={product}>
                  {product}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={ratingFilter}
            onValueChange={(value: RatingFilter) => setRatingFilter(value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Rating" />
            </SelectTrigger>
            <SelectContent>
              {ratingOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={typeFilter}
            onValueChange={(value: TypeFilter) => setTypeFilter(value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={timeframe}
            onValueChange={(value: TimeframeFilter) => setTimeframe(value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Timeframe" />
            </SelectTrigger>
            <SelectContent>
              {timeframeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ScrollArea className="rounded-xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <TableRow>
              <TableHead className="px-4 py-3">Submitted</TableHead>
              <TableHead className="px-4 py-3">Type</TableHead>
              <TableHead className="px-4 py-3">Rating</TableHead>
              <TableHead className="px-4 py-3">Comment</TableHead>
              <TableHead className="px-4 py-3">Page</TableHead>
              <TableHead className="px-4 py-3">Reporter</TableHead>
              <TableHead className="px-4 py-3">Attachments</TableHead>
              <TableHead className="px-4 py-3 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-sm">
            {filteredInsights.map((insight) => {
              const hasScreenshot = Boolean(insight.screenshot);
              const hasHar = Boolean(insight.harFile);
              const hasConsoleLogs = Boolean(insight.consoleLogs?.length);
              const hasNetworkLogs = Boolean(insight.networkRequests?.length);

              return (
                <TableRow key={insight.id} className="align-top">
                  <TableCell className="px-4 py-4">
                    <div className="font-medium text-foreground">
                      {formatDateTime(insight.submittedAt)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatRelativeTime(insight.submittedAt)}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-4">
                    <Badge
                      variant={
                        insight.type === "bug" ? "destructive" : "secondary"
                      }
                      className="px-3 py-1 text-xs"
                    >
                      {insight.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-4 py-4 font-medium text-foreground">
                    {getRatingDisplay(insight.rating ?? null)}
                  </TableCell>
                  <TableCell className="px-4 py-4 max-w-xs">
                    <p className="text-sm text-foreground line-clamp-3">
                      {insight.comment}
                    </p>
                    {insight.labels && insight.labels.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {insight.labels.map((label: string) => (
                          <Badge
                            key={label}
                            variant="outline"
                            className="px-2 py-1"
                          >
                            {label}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm text-muted-foreground">
                    {insight.pageUrl}
                  </TableCell>
                  <TableCell className="px-4 py-4 text-sm text-muted-foreground">
                    {insight.email}
                  </TableCell>
                  <TableCell className="px-4 py-4">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {hasScreenshot && (
                        <Badge variant="outline">Screenshot</Badge>
                      )}
                      {hasHar && <Badge variant="outline">HAR</Badge>}
                      {hasConsoleLogs && (
                        <Badge variant="outline">Console</Badge>
                      )}
                      {hasNetworkLogs && (
                        <Badge variant="outline">Network</Badge>
                      )}
                      {!hasScreenshot &&
                        !hasHar &&
                        !hasConsoleLogs &&
                        !hasNetworkLogs && (
                          <span className="text-muted-foreground">—</span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          disabled={!hasScreenshot}
                          onSelect={() => {
                            if (!insight.screenshot) return;
                            setPreview({
                              image: insight.screenshot,
                              page: insight.pageUrl,
                              email: insight.email,
                            });
                          }}
                        >
                          View screenshot
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!hasHar}
                          onSelect={() =>
                            insight.harFile &&
                            handleDownloadHar(insight.harFile, insight.id)
                          }
                        >
                          Download HAR
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!hasConsoleLogs}
                          onSelect={() =>
                            insight.consoleLogs &&
                            handleDownloadJson(
                              insight.consoleLogs,
                              insight.id,
                              "console",
                            )
                          }
                        >
                          Download console logs
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!hasNetworkLogs}
                          onSelect={() =>
                            insight.networkRequests &&
                            handleDownloadJson(
                              insight.networkRequests,
                              insight.id,
                              "network",
                            )
                          }
                        >
                          Download network data
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredInsights.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="px-4 py-6">
                  <Alert className="mx-auto max-w-md">
                    <AlertDescription>
                      No insights match the selected filters yet. Submit
                      feedback through the widget to populate this view.
                    </AlertDescription>
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      <Dialog
        open={Boolean(preview)}
        onOpenChange={(open) => !open && setPreview(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Screenshot preview</DialogTitle>
            <DialogDescription>
              {preview?.page} • {preview?.email}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <img
                src={preview.image}
                alt="Feedback screenshot"
                className="max-h-[70vh] w-full rounded-md object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default FeedbackInsightsPage;
